import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { locationsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, PencilSimple, Trash, MapPin, PlusCircle, X,
  Buildings, Hash, Eye,
} from '@phosphor-icons/react';

const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala',
  'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha',
  'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
  'Uttarakhand', 'West Bengal', 'Chandigarh', 'Jammu and Kashmir', 'Ladakh',
  'Puducherry', 'Andaman and Nicobar Islands', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep',
];

const DEFAULT_MODULES = [
  { module: 'Self-Invoice', prefix: '', starting_number: '1' },
  { module: 'Credit Note', prefix: '', starting_number: '00001' },
  { module: 'Customer Payment', prefix: '', starting_number: '00001' },
  { module: 'Purchase Order', prefix: '', starting_number: '00001' },
  { module: 'Sales Order', prefix: '', starting_number: '00001' },
  { module: 'Vendor Payment', prefix: '', starting_number: '0001' },
  { module: 'Retainer Invoice', prefix: '', starting_number: '00001' },
  { module: 'Bill Of Supply', prefix: '', starting_number: '000001' },
  { module: 'Invoice', prefix: '', starting_number: '0001' },
  { module: 'Delivery Challan', prefix: '', starting_number: '00001' },
];

const FormField = ({ label, children, required }) => (
  <div>
    <label className="text-xs text-[#64748B] block mb-1">{label}{required && ' *'}</label>
    {children}
  </div>
);

export default function LocationMasterPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewLoc, setViewLoc] = useState(null);
  const [showSeriesDialog, setShowSeriesDialog] = useState(false);
  const [seriesLoc, setSeriesLoc] = useState(null);
  const [editSeries, setEditSeries] = useState(null);

  const [form, setForm] = useState({
    name: '', gstin: '',
    address: { attention: '', street1: '', street2: '', city: '', pincode: '', state: '' },
  });

  const [seriesForm, setSeriesForm] = useState({
    series_name: '',
    modules: DEFAULT_MODULES.map(m => ({ ...m })),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await locationsAPI.list();
      setLocations(data.locations || []);
    } catch { toast.error('Failed to load locations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    name: '', gstin: '',
    address: { attention: '', street1: '', street2: '', city: '', pincode: '', state: '' },
  });

  const openCreate = () => { setEditLoc(null); resetForm(); setShowForm(true); };

  const openEdit = (loc) => {
    setEditLoc(loc);
    setForm({
      name: loc.name || '',
      gstin: loc.gstin || '',
      address: {
        attention: loc.address?.attention || '',
        street1: loc.address?.street1 || '',
        street2: loc.address?.street2 || '',
        city: loc.address?.city || '',
        pincode: loc.address?.pincode || '',
        state: loc.address?.state || '',
      },
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Location name is required'); return; }
    setSaving(true);
    try {
      if (editLoc) {
        await locationsAPI.update(editLoc.id, form);
        toast.success('Location updated');
      } else {
        await locationsAPI.create(form);
        toast.success('Location created');
      }
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await locationsAPI.delete(id);
      toast.success('Location deleted');
      setDeleteConfirm(null);
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const openAddSeries = (loc) => {
    setSeriesLoc(loc);
    setEditSeries(null);
    setSeriesForm({ series_name: '', modules: DEFAULT_MODULES.map(m => ({ ...m })) });
    setShowSeriesDialog(true);
  };

  const openEditSeries = (loc, series) => {
    setSeriesLoc(loc);
    setEditSeries(series);
    setSeriesForm({
      series_name: series.series_name || '',
      modules: series.modules?.map(m => ({ ...m })) || DEFAULT_MODULES.map(m => ({ ...m })),
    });
    setShowSeriesDialog(true);
  };

  const handleSaveSeries = async () => {
    if (!seriesForm.series_name.trim()) { toast.error('Series name is required'); return; }
    if (!seriesLoc) return;
    setSaving(true);
    try {
      if (editSeries) {
        await locationsAPI.updateSeries(seriesLoc.id, editSeries.id, seriesForm);
        toast.success('Series updated');
      } else {
        await locationsAPI.addSeries(seriesLoc.id, seriesForm);
        toast.success('Transaction series added');
      }
      setShowSeriesDialog(false);
      load();
      if (viewLoc) {
        const { data } = await locationsAPI.get(viewLoc.id);
        setViewLoc(data);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save series'); }
    finally { setSaving(false); }
  };

  const handleDeleteSeries = async (loc, seriesId) => {
    try {
      await locationsAPI.deleteSeries(loc.id, seriesId);
      toast.success('Series deleted');
      load();
      if (viewLoc) {
        const { data } = await locationsAPI.get(viewLoc.id);
        setViewLoc(data);
      }
    } catch { toast.error('Failed to delete series'); }
  };

  const updateSeriesModule = (idx, field, value) => {
    setSeriesForm(prev => ({
      ...prev,
      modules: prev.modules.map((m, i) => i === idx ? { ...m, [field]: value } : m),
    }));
  };

  const setAddr = (field, value) => {
    setForm(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
  };

  return (
    <div className="p-6" data-testid="location-master-page">
      <PageHeader
        title="Location Master"
        subtitle="Manage business locations and transaction number series"
        actions={
          <Button onClick={openCreate} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="add-location-btn">
            <Plus size={16} className="mr-1.5" /> Add Location
          </Button>
        }
      />

      <div className="mt-6 space-y-4">
        <p className="text-sm text-[#64748B]">{locations.length} location{locations.length !== 1 ? 's' : ''}</p>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="locations-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] font-medium text-xs">
                <th className="text-left p-3">Location Name</th>
                <th className="text-left p-3">City</th>
                <th className="text-left p-3">State</th>
                <th className="text-left p-3">GSTIN</th>
                <th className="text-center p-3">Series</th>
                <th className="text-center p-3">Status</th>
                <th className="text-center p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8"><div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" /></td></tr>
              ) : locations.length > 0 ? locations.map(loc => (
                <tr key={loc.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`loc-row-${loc.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <MapPin size={16} className="text-[#0F4C81] flex-shrink-0" />
                      <span className="font-medium text-[#0F172A]">{loc.name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-[#64748B]">{loc.address?.city || '-'}</td>
                  <td className="p-3 text-[#64748B]">{loc.address?.state || '-'}</td>
                  <td className="p-3 text-xs font-mono text-[#64748B]">{loc.gstin || '-'}</td>
                  <td className="p-3 text-center">
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                      {loc.transaction_series?.length || 0}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${loc.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                      {loc.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewLoc(loc)} title="View Details" data-testid={`view-loc-${loc.id}`}>
                        <Eye size={15} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openAddSeries(loc)} title="Add Series" data-testid={`add-series-${loc.id}`}>
                        <Hash size={15} className="text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(loc)} data-testid={`edit-loc-${loc.id}`}>
                        <PencilSimple size={15} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteConfirm(loc)} data-testid={`delete-loc-${loc.id}`}>
                        <Trash size={15} />
                      </Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No locations yet. Click "Add Location" to create one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Location Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="location-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Buildings size={20} />{editLoc ? 'Edit Location' : 'Add New Location'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <FormField label="Location Name" required>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. HearClear Delhi" data-testid="loc-name" />
            </FormField>

            <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-3">
              <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Address</p>
              <FormField label="Attention">
                <Input value={form.address.attention} onChange={e => setAddr('attention', e.target.value)} placeholder="Attention / Contact Person" data-testid="loc-attention" />
              </FormField>
              <FormField label="Street 1">
                <Input value={form.address.street1} onChange={e => setAddr('street1', e.target.value)} placeholder="Street address line 1" data-testid="loc-street1" />
              </FormField>
              <FormField label="Street 2">
                <Input value={form.address.street2} onChange={e => setAddr('street2', e.target.value)} placeholder="Street address line 2" data-testid="loc-street2" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="City">
                  <Input value={form.address.city} onChange={e => setAddr('city', e.target.value)} placeholder="City" data-testid="loc-city" />
                </FormField>
                <FormField label="Pincode">
                  <Input value={form.address.pincode} onChange={e => setAddr('pincode', e.target.value)} placeholder="110001" data-testid="loc-pincode" />
                </FormField>
              </div>
              <FormField label="State">
                <Select value={form.address.state} onValueChange={v => setAddr('state', v)}>
                  <SelectTrigger data-testid="loc-state"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>
                    {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <FormField label="GSTIN">
              <Input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} placeholder="22AAAAA0000A1Z5" className="font-mono" data-testid="loc-gstin" />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="loc-save-btn">
              {saving ? 'Saving...' : editLoc ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Location Detail Dialog */}
      <Dialog open={!!viewLoc} onOpenChange={o => { if (!o) setViewLoc(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="location-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <MapPin size={20} className="text-[#0F4C81]" />{viewLoc?.name}
            </DialogTitle>
          </DialogHeader>
          {viewLoc && (
            <div className="space-y-5 py-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-[#64748B] mb-1">Address</p>
                  <p className="text-[#0F172A]">
                    {[viewLoc.address?.attention, viewLoc.address?.street1, viewLoc.address?.street2, viewLoc.address?.city, viewLoc.address?.state, viewLoc.address?.pincode].filter(Boolean).join(', ') || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#64748B] mb-1">GSTIN</p>
                  <p className="font-mono text-[#0F172A]">{viewLoc.gstin || '-'}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#0F172A]">Transaction Number Series</h3>
                  <Button size="sm" variant="outline" onClick={() => openAddSeries(viewLoc)} data-testid="add-series-from-detail">
                    <PlusCircle size={14} className="mr-1" /> Add Series
                  </Button>
                </div>

                {viewLoc.transaction_series?.length > 0 ? (
                  <div className="space-y-3">
                    {viewLoc.transaction_series.map(series => (
                      <div key={series.id} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`series-block-${series.id}`}>
                        <div className="bg-[#F8FAFC] px-4 py-2 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium text-[#0F172A]">{series.series_name}</span>
                            <span className="ml-2 text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{series.state_code}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEditSeries(viewLoc, series)} data-testid={`edit-series-${series.id}`}>
                              <PencilSimple size={13} />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDeleteSeries(viewLoc, series.id)} data-testid={`delete-series-${series.id}`}>
                              <Trash size={13} />
                            </Button>
                          </div>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[#64748B] border-b border-[#E2E8F0]">
                              <th className="text-left px-4 py-1.5 font-medium">Module</th>
                              <th className="text-left px-4 py-1.5 font-medium">Prefix</th>
                              <th className="text-left px-4 py-1.5 font-medium">Starting Number</th>
                            </tr>
                          </thead>
                          <tbody>
                            {series.modules?.map((m, i) => (
                              <tr key={i} className="border-b border-[#F8FAFC] last:border-0">
                                <td className="px-4 py-1.5 text-[#0F172A]">{m.module}</td>
                                <td className="px-4 py-1.5 font-mono text-[#64748B]">{m.prefix || '-'}</td>
                                <td className="px-4 py-1.5 font-mono">{m.starting_number}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#64748B] bg-[#F8FAFC] rounded-lg p-4">No transaction series configured. Click "Add Series" to set up number prefixes.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Series Dialog */}
      <Dialog open={showSeriesDialog} onOpenChange={setShowSeriesDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="series-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editSeries ? 'Edit Transaction Series' : 'Transaction Series Preferences'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <FormField label="Series Name" required>
              <Select value={seriesForm.series_name} onValueChange={v => setSeriesForm(f => ({ ...f, series_name: v }))}>
                <SelectTrigger data-testid="series-name"><SelectValue placeholder="Select state for series..." /></SelectTrigger>
                <SelectContent>
                  {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>

            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="series-modules-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-xs text-[#64748B] font-semibold uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 w-[200px]">Module</th>
                    <th className="text-left px-4 py-2.5">Prefix</th>
                    <th className="text-left px-4 py-2.5 w-[140px]">Starting Number</th>
                  </tr>
                </thead>
                <tbody>
                  {seriesForm.modules.map((m, idx) => (
                    <tr key={m.module} className="border-t border-[#E2E8F0]" data-testid={`series-row-${idx}`}>
                      <td className="px-4 py-2 text-[#0F172A] font-medium text-sm">{m.module}</td>
                      <td className="px-4 py-2">
                        <Input
                          value={m.prefix}
                          onChange={e => updateSeriesModule(idx, 'prefix', e.target.value)}
                          placeholder={`e.g. ${m.module.split(' ').map(w => w[0]).join('')}-DL-2627-`}
                          className="h-8 text-xs font-mono"
                          data-testid={`series-prefix-${idx}`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          value={m.starting_number}
                          onChange={e => updateSeriesModule(idx, 'starting_number', e.target.value)}
                          className="h-8 text-xs font-mono w-[120px]"
                          data-testid={`series-start-${idx}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSeriesDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSeries} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="series-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={o => { if (!o) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm" data-testid="delete-location-dialog">
          <DialogHeader><DialogTitle className="font-['Plus_Jakarta_Sans']">Delete Location</DialogTitle></DialogHeader>
          <p className="text-sm text-[#64748B] py-2">Delete <strong>{deleteConfirm?.name}</strong>? This will also remove all transaction series. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-location">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
